import { createContext, useContext, ReactNode } from 'react';
import { SimpleMessages } from '../utils/messages';
import { Message } from '@ai-sdk/react';

export interface ChatManager {
	beforeMessageSent?: (
		message: string,
		options: { getCurrentMessages: () => Message[] },
	) => Promise<boolean>;
	onResponseReceived?: (message: string) => Promise<void>;
	onStatusChanged?: (status: string) => void;
	onError?: (error: Error) => void;
	initialMessages?: SimpleMessages;
	initialInput?: string;
	chatId?: string;
}

export interface FeatureManager {
	isAudioUploadEnabled?: boolean;
	isDocumentUploadEnabled?: boolean;
	isQuickPromptsEnabled?: boolean;
	isChatHistoryEnabled?: boolean;
	/**
	 * Controls the large center mic ("बोलना शुरू करे") shown in empty state.
	 * Default: enabled (unless explicitly set to false).
	 */
	isCenterMicEnabled?: boolean;
	/**
	 * Controls the marquee/bullet strip shown under the heading in empty state.
	 * Default: enabled (unless explicitly set to false).
	 */
	isIntroMarqueeEnabled?: boolean;
}

export interface IConfigContext {
	appTitle?: string;
	isStandalone: boolean;
	isAndroidApp?: boolean;
	baseRoute: string;
	customBodyParams?: {
		additional_context?: string;
		[key: string]: Object;
	};
	getIsInternalRoute: (link: string) => boolean;
	postUserGuide?: () => void;
	checkTourStatus?: () => Promise<boolean>;
	userMeta: {
		location?: {
			lat: number;
			long: number;
		};
		langCode?: 'en' | 'hi' | 'mr';
	};
	customHeaders: Record<string, string>;
	featureManager?: FeatureManager;
	chatManager?: ChatManager;
	/** When set (e.g. from FAQ question screen for a feature), show this category's example prompts directly instead of feature name chips */
	initialPromptCategory?: string;
}
const ConfigContext = createContext<IConfigContext | null>(null);

export const useConfigContext = (): IConfigContext => {
	const context = useContext(ConfigContext);
	if (!context) {
		throw new Error('useConfigContext must be used within an ConfigProvider');
	}
	return context;
};

interface ConfigProviderProps extends IConfigContext {
	children: ReactNode;
}

export const ConfigProvider = ({
	children,
	...configs
}: ConfigProviderProps) => {
	return (
		<ConfigContext.Provider value={configs}>{children}</ConfigContext.Provider>
	);
};
